import { apiSurveys } from "@/api/survey.api.ts";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { ReminderHistoryModal } from "@/components/reminder/ReminderHistoryModal.tsx";
import { ReminderSendModal } from "@/components/reminder/ReminderSendModal.tsx";
import { SurveyReopenModal } from "@/pages/talent/surveys/view/widgets/SurveyReopenModal.tsx";
import { ReminderEntityType } from "@/types/reminder.types.ts";
import {
  ConfirmationModal,
  Page,
  TabItem,
  Tabs,
  useModals,
  Button,
  ButtonIcon,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useSurveysHook } from "@/hooks/use-surveys.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { SurveyEditPageStep } from "@/pages/talent/surveys/edit/survey-edit.ts";
import { SurveyParticipants } from "@/pages/talent/surveys/view/components/SurveyParticipants.tsx";
import { SurveySummary } from "@/pages/talent/surveys/view/components/SurveySummary.tsx";
import {
  useSurveyAggregatedResults,
  useSurveyComplete,
  useSurveyDetails,
  useSurveyDuplicate,
  useSurveyLaunch,
  useSurveyPause,
} from "@/queries/surveys/use-surveys.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiExportFormat } from "@/types/analytics/analytics.types.ts";
import { ApiSorting, ApiSortingDirection } from "@/types/api.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiSurveyParticipantFilter, ApiSurveyStatus } from "@/types/survey.types.ts";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

enum SurveyViewPageStep {
  summary = "summary",
  participants = "participants",
}

export const SurveysView = () => {
  const { id, step } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { renderDate } = useFormatter();
  const { openModal } = useModals();
  const { canIEdit } = usePermissions();
  const { handleDeleteSurvey, isDeletePending } = useSurveysHook();
  const library = filtersLibrary();
  const { data: survey } = useSurveyDetails(id ? parseInt(id) : undefined, true);

  const {
    filter: { filter: participantFilter, count: participantFilterCount, typedFilters },
    filterButton: participantFilterButton,
    onApplySorting,
  } = useAppFilter<ApiSurveyParticipantFilter>({
    filters: [
      library.page_number,
      library.text,
      library.job_profile_ids,
      library.job_role_ids,
      library.department_ids,
      library.division_ids,
      library.team_ids,
      library.location_ids,
      library.employment_type_ids,
      ...(survey?.anonymous_threshold === 0 ? [library.participant_statuses] : []),
      {
        ...library.sort,
        defaultValue: {
          fields: [{ field: "employee_name", direction: ApiSortingDirection.asc }],
        } as ApiSorting,
      },
    ],
    source: "Survey Participants",
  });

  const currentStep = useMemo(() => {
    if (step) {
      return step as SurveyViewPageStep;
    }
    return SurveyEditPageStep.details;
  }, [step]);

  const completeSurvey = useSurveyComplete();
  const surveyDuplicate = useSurveyDuplicate();
  const { data: surveyResults } = useSurveyAggregatedResults(survey?.id, {});
  const { isExporting, downloadFile } = useDownloadHelper();

  const launchSurvey = useSurveyLaunch();
  const pauseSurvey = useSurveyPause();

  useTitle(survey?.title, t("surveys:survey"));

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showMore = queryParams.get("show-more") === "true";

  useEffect(() => {
    analyticsService.trackEvent(analyticEvents.surveys.viewedSurvey, {
      [analyticProperties.id]: id,
      [analyticProperties.tab]: currentStep,
    });
  }, [id, currentStep]);

  const handleCompleteSurvey = async () => {
    openModal(ConfirmationModal, {
      question: t("surveys:complete_survey_question"),
      text: t("surveys:complete_survey_confirm"),
      acceptTitle: t("surveys:complete_survey"),
      onAccept: async function () {
        if (!survey) return;
        try {
          await completeSurvey.mutateAsync({ id: survey.id });
          analyticsService.trackEvent(analyticEvents.surveys.completed, {
            [analyticProperties.id]: survey.id,
          });
        } catch {
          return;
        }
      },
    });
  };

  const handleReopenSurvey = () => {
    if (!survey) return;
    openModal(SurveyReopenModal, {
      survey,
      onSuccess: () => {
        analyticsService.trackEvent(analyticEvents.surveys.reopened, {
          [analyticProperties.id]: survey.id,
        });
      },
    });
  };

  const handleDuplicateSurvey = async () => {
    openModal(ConfirmationModal, {
      question: t("surveys:duplicate_survey"),
      text: t("surveys:duplicate_survey_confirm"),
      acceptTitle: t("surveys:duplicate_survey"),
      onAccept: async function () {
        if (!survey) return;
        try {
          const data = await surveyDuplicate.mutateAsync({ id: survey.id });
          analyticsService.trackEvent(analyticEvents.surveys.duplicated, {
            [analyticProperties.id]: data.id,
          });
          navigate(`/surveys/${data.id}/setup/details`);
        } catch {
          return;
        }
      },
    });
  };

  const handleDelete = () => {
    if (!survey) return;

    handleDeleteSurvey(survey, () => {
      navigate("/surveys");
    });
  };

  const handleExportClick = useCallback(
    (format: ApiExportFormat) => {
      if (!survey) return;
      downloadFile({
        apiCall: () => apiSurveys.exportSurveyResults(survey.id, { format }),
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.surveys.surveyResultsExported, {
            [analyticProperties.id]: survey?.id,
            [analyticProperties.format]: format,
          });
        },
      });
    },
    [survey]
  );

  const handleLaunchSurvey = async () => {
    if (!survey) return;

    try {
      await launchSurvey.mutateAsync({ id: survey.id });

      analyticsService.trackEvent(analyticEvents.surveys.launched, {
        [analyticProperties.id]: survey.id,
      });
    } catch {
      // Handle error if needed
    }
  };

  const handlePauseSurvey = async () => {
    if (!survey) return;

    try {
      await pauseSurvey.mutateAsync({ id: survey.id });

      analyticsService.trackEvent(analyticEvents.surveys.paused, {
        [analyticProperties.id]: survey.id,
      });
    } catch {
      // Handle error if needed
    }
  };

  return (
    <>
      <Page>
        <Page.Header
          title={survey?.title || "..."}
          showBack={true}
          navigateTo={"/surveys"}
          titleSlot={
            survey && (
              <>
                <Tooltip>
                  <TooltipTrigger className={"print:hidden"}>
                    <FontAwesomeIcon icon={"fa-light fa-circle-info"} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Stack>
                      <span>
                        {survey.anonymous_threshold > 0
                          ? t("common:anonymous")
                          : t("common:non_anonymous")}
                      </span>
                      {survey.date_starts_at && survey.date_ends_at && (
                        <span>{` ${renderDate(survey.date_starts_at)} - ${renderDate(survey.date_ends_at)}`}</span>
                      )}
                    </Stack>
                  </TooltipContent>
                </Tooltip>
              </>
            )
          }
        >
          <Stack direction={"horizontal"} gap={"sm"} items={"center"} className={"overflow-hidden"}>
            {survey && (
              <>
                {survey.status !== ApiSurveyStatus.completed && (
                  <DropdownMenu
                    trigger={
                      <ButtonIcon
                        icon={<FontAwesomeIcon icon={"fa-light fa-bell"} />}
                        variant={"secondary"}
                      />
                    }
                  >
                    <DropdownMenuItem
                      onClick={() =>
                        openModal(ReminderSendModal, {
                          recipients: [],
                          entityType: ReminderEntityType.survey,
                          entityIds: [survey.id],
                        })
                      }
                      title={t("core:remind_everyone") + "..."}
                    />
                    <DropdownMenuItem
                      onClick={() =>
                        openModal(ReminderHistoryModal, {
                          filter: {
                            entity_type: ReminderEntityType.survey,
                            entity_ids: [survey.id],
                          },
                        })
                      }
                      title={t("core:reminder_history")}
                    />
                  </DropdownMenu>
                )}

                {survey.acl.can_edit && (
                  <>
                    {[ApiSurveyStatus.draft, ApiSurveyStatus.paused].includes(survey.status) && (
                      <Button
                        isLoading={launchSurvey.isPending}
                        onClick={handleLaunchSurvey}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-play" />}
                      >
                        {t("surveys:launch_survey")}
                      </Button>
                    )}
                    {[ApiSurveyStatus.running, ApiSurveyStatus.pending_launch].includes(
                      survey.status
                    ) && (
                      <Button
                        isLoading={pauseSurvey.isPending}
                        onClick={handlePauseSurvey}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-pause" />}
                      >
                        {t("surveys:pause_survey")}
                      </Button>
                    )}
                    {currentStep == SurveyViewPageStep.participants && participantFilterButton()}
                    <ButtonLink variant={"secondary"} href={`/surveys/${survey.id}/setup`}>
                      {t("surveys:edit_survey")}
                    </ButtonLink>
                  </>
                )}

                <DropdownMenu className={"print:hidden"}>
                  {survey.acl.can_edit &&
                    [
                      ApiSurveyStatus.pending_launch,
                      ApiSurveyStatus.running,
                      ApiSurveyStatus.paused,
                    ].includes(survey.status) && (
                      <DropdownMenuItem
                        disabled={completeSurvey.isPending}
                        onClick={handleCompleteSurvey}
                        title={`${t("surveys:complete_survey")}...`}
                      />
                    )}
                  {survey.acl.can_edit && [ApiSurveyStatus.completed].includes(survey.status) && (
                    <DropdownMenuItem
                      onClick={handleReopenSurvey}
                      title={`${t("surveys:reopen_survey")}...`}
                    />
                  )}
                  {canIEdit(ApiPermission.surveys_create) && (
                    <DropdownMenuItem
                      disabled={surveyDuplicate.isPending}
                      onClick={handleDuplicateSurvey}
                      title={`${t("surveys:duplicate_survey")}...`}
                    />
                  )}
                  {survey.participants_stats.completed_count > 0 &&
                    surveyResults &&
                    !surveyResults.results.is_below_anonymity_threshold && (
                      <DropdownMenuSub title={`${t("common:export")}...`} disabled={isExporting}>
                        <DropdownMenuItem
                          disabled={isExporting}
                          onClick={() => handleExportClick(ApiExportFormat.xlsx)}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-file-xls" />}
                          title={t("common:export_to_xlsx")}
                        />
                        <DropdownMenuItem
                          disabled={isExporting}
                          onClick={() => handleExportClick(ApiExportFormat.pdf)}
                          leftIcon={<FontAwesomeIcon icon="fa-light fa-file-pdf" />}
                          title={t("common:export_to_pdf")}
                        />
                      </DropdownMenuSub>
                    )}

                  {survey.acl.can_edit && (
                    <DropdownMenuItem
                      variant={"menuDanger"}
                      onClick={handleDelete}
                      disabled={isDeletePending}
                      title={`${t("common:delete")}...`}
                    />
                  )}
                </DropdownMenu>
              </>
            )}
          </Stack>
        </Page.Header>

        <Page.Content>
          {survey && (
            <>
              <Tabs
                className={"print:hidden"}
                onActiveTabChange={(activeTab) => {
                  if (activeTab == 0) {
                    navigate(`/surveys/${survey.id}/view/${SurveyViewPageStep.summary}`);
                  } else {
                    navigate(`/surveys/${survey.id}/view/${SurveyViewPageStep.participants}`);
                  }
                }}
                activeTab={currentStep == SurveyViewPageStep.summary ? 0 : 1}
              >
                <TabItem title={t("common:summary")}></TabItem>
                <TabItem title={t("common:participation")}></TabItem>
              </Tabs>

              {currentStep == SurveyViewPageStep.summary && (
                <SurveySummary survey={survey} show_more={showMore} />
              )}
              {currentStep == SurveyViewPageStep.participants && (
                <SurveyParticipants
                  survey={survey}
                  filter={participantFilter}
                  filterCount={participantFilterCount}
                  sorting={typedFilters.sorting}
                  onApplySorting={onApplySorting}
                />
              )}
            </>
          )}
        </Page.Content>
      </Page>
    </>
  );
};
